import * as React from "react"
import { reportActivityError, trackActivity, trackPaymentStarted } from "../activity"
import { resultError, type StripeExpressCheckoutConfirmEvent } from "../stripe"
import { markPaymentSubmitted } from "../bookingSession"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutSession } from "./useCheckoutSession"
import type { useCheckoutState } from "./useCheckoutState"
import type { useCustomerDetails } from "./useCustomerDetails"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "journeyStep" | "stripeReady" | "checkoutRef" | "paymentMountRef" | "paymentElementRef" | "expressMountRef" | "setWalletAvailable" | "expressElementRef" | "confirmPaymentRef" | "quote" | "actionsRef" | "setError" | "setPhase" | "confirmationToken" | "checkoutGeneration" | "setSavedDetailsFingerprint" | "detailsFingerprint" | "differentBilling" | "billingComplete">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "calendarReady" | "calendarRangeError">
    & Pick<ReturnType<typeof useCustomerDetails>,
        "saveCustomerDetails" | "validateDetails">
    & Pick<ReturnType<typeof useCheckoutSession>,
        "pollStatus">

export function useCheckoutPayment(inputs: Inputs) {
    const {
        journeyStep,
        stripeReady,
        checkoutRef,
        paymentMountRef,
        paymentElementRef,
        expressMountRef,
        setWalletAvailable,
        expressElementRef,
        confirmPaymentRef,
        quote,
        calendarReady,
        calendarRangeError,
        actionsRef,
        setError,
        setPhase,
        saveCustomerDetails,
        pollStatus,
        confirmationToken,
        checkoutGeneration,
        setSavedDetailsFingerprint,
        detailsFingerprint,
        validateDetails,
        differentBilling,
        billingComplete,
    } = inputs
    const [paymentComplete, setPaymentComplete] = React.useState(false)
    React.useEffect(() => {
        setPaymentComplete(false)
        if (journeyStep !== "payment" || !stripeReady || !checkoutRef.current || !paymentMountRef.current) return
        const payment = checkoutRef.current.createPaymentElement({
            fields: {
                billingDetails: {
                    name: "always",
                    email: "never",
                    phone: "never",
                    address: "never",
                },
            },
            wallets: { applePay: "never", googlePay: "never", link: "never" },
            layout: { type: "accordion", defaultCollapsed: false, radios: "always" },
        })
        paymentElementRef.current = payment
        // Local contact/address fields are sent to Stripe on submission. Use the
        // Element's completion event here, so session.canConfirm cannot block
        // the button while those separately collected details are still unsaved.
        payment.on("change", (event) => {
            if (paymentElementRef.current === payment) setPaymentComplete(event.complete === true)
        })
        payment.on("loaderror", () => {
            if (paymentElementRef.current === payment) setPaymentComplete(false)
        })
        payment.mount(paymentMountRef.current)
        return () => {
            setPaymentComplete(false)
            payment.unmount()
            payment.destroy?.()
            if (paymentElementRef.current === payment) paymentElementRef.current = null
        }
    }, [journeyStep, stripeReady])

    React.useEffect(() => {
        if (journeyStep !== "payment" || !stripeReady || !checkoutRef.current || !expressMountRef.current) {
            setWalletAvailable(false)
            return
        }
        setWalletAvailable(null)
        const express = checkoutRef.current.createExpressCheckoutElement({
            buttonHeight: 52,
            // Stripe requires unlimited rows when the overflow menu is disabled.
            layout: { maxColumns: 1, overflow: "never" },
            paymentMethods: { applePay: "always", link: "never" },
        })
        expressElementRef.current = express
        express.on("availablepaymentmethodschange", (event) => {
            setWalletAvailable(Object.values(event.paymentMethods ?? {}).some((method) => method?.available === true))
        })
        express.on("click", (event) => {
            // Stripe requires resolve when a click listener is registered.
            // Open the wallet immediately; analytics cannot block it.
            event.resolve()
            trackPaymentStarted(event.expressPaymentType)
        })
        express.on("confirm", (event) => void confirmPaymentRef.current(event))
        express.mount(expressMountRef.current)
        return () => {
            express.unmount()
            express.destroy?.()
            if (expressElementRef.current === express) expressElementRef.current = null
        }
    }, [journeyStep, stripeReady])

    const paymentInFlight = React.useRef(false)
    const confirmPayment = async (expressEvent?: StripeExpressCheckoutConfirmEvent) => {
        // A repeated callback must not fail a payment that is already in progress.
        if (paymentInFlight.current) return
        const failWallet = (message: string) => {
            try {
                expressEvent?.paymentFailed({ reason: "fail", message })
            } catch {
                // A dismissed wallet must not prevent recovery of the checkout form.
            }
        }
        if (!quote || !calendarReady || calendarRangeError || !stripeReady) {
            if (expressEvent) {
                const message = "Check your booking details and try again."
                setError(message)
                failWallet(message)
            }
            return
        }
        if (!expressEvent && (!paymentComplete || (differentBilling && !billingComplete))) return
        setError("")
        if (!validateDetails()) { failWallet("Check your delivery details and try again."); return }
        const actions = actionsRef.current
        if (!actions) {
            reportActivityError("PAYMENT_NOT_READY")
            setError("Payment is still loading.")
            failWallet("Payment is still loading. Please try again.")
            return
        }
        const generation = checkoutGeneration.current
        paymentInFlight.current = true
        if (!expressEvent) trackPaymentStarted("card")
        let confirmationStarted = false
        setPhase("paying")
        try {
            if (!expressEvent) {
                const validation = await actions.validateElements()
                if (generation !== checkoutGeneration.current) return
                const validationError = resultError(validation)
                if (validationError) { setError(validationError); setPhase("ready"); return }
            }
            // Stripe keeps the cardholder name private until confirmation. The
            // signed webhook supplies it when completing the booking.
            await saveCustomerDetails(true, Boolean(expressEvent))
            if (generation !== checkoutGeneration.current) {
                failWallet("Your booking changed. Please try again.")
                return
            }
            setSavedDetailsFingerprint(detailsFingerprint)
            trackActivity("details_saved", {}, true)
            trackActivity("payment_attempted", { payment_method: expressEvent ? "wallet" : "card" })
            // From here Stripe owns the wallet outcome, including bank authentication.
            confirmationStarted = true
            const result = await actions.confirm(expressEvent ? { expressCheckoutConfirmEvent: expressEvent } : undefined)
            if (generation !== checkoutGeneration.current) return
            const message = resultError(result)
            if (message) {
                const technical = ['api_error', 'api_connection_error', 'invalid_request_error'].includes(result.error?.type || '')
                trackActivity('payment_failed', { code: result.error?.code || (technical ? 'STRIPE_API_ERROR' : 'PAYMENT_DECLINED') })
                if (technical) reportActivityError('STRIPE_API_ERROR')
                setError(message); setPhase('ready'); return
            }
            markPaymentSubmitted(confirmationToken)
            void pollStatus(confirmationToken)
        } catch (reason) {
            const message = reason instanceof Error ? reason.message : "Payment could not be completed."
            if (!confirmationStarted) failWallet(message)
            if (generation !== checkoutGeneration.current) return
            trackActivity("payment_failed", { code: "PAYMENT_CONFIRM_FAILED" })
            if (!(reason as { activityReported?: boolean })?.activityReported) reportActivityError("PAYMENT_CONFIRM_FAILED")
            setError(message)
            setPhase("ready")
        } finally {
            paymentInFlight.current = false
        }
    }
    confirmPaymentRef.current = confirmPayment

    return {
        paymentComplete,
        confirmPayment,
    }
}
